import { ValidationResponse } from "../../domain/entities/ValidationEntity";
// import { API_BASE_URL, CORE_API_URL } from "../../../../config/api";
import { CORE_API_URL } from "../../../../config/api";

// HTTP Status Codes
const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
};

// API Error Class
class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

// Helper functions (getStoredToken used by commented getVendorById rollback path)
// const getStoredToken = () => {
//   try {
//     return localStorage.getItem("access_token");
//   } catch (error) {
//     console.error("Error getting stored token:", error);
//     return null;
//   }
// };

const getStoredCompanyData = () => {
  try {
    const companyData = localStorage.getItem("company_data");
    return companyData ? JSON.parse(companyData) : null;
  } catch (error) {
    console.error("Error getting stored company data:", error);
    return null;
  }
};

// Previously fetched vendor master data here and forwarded it in bill_data.
// bill_validations.php now resolves vendor_gst, gst_treatment, source_of_supply,
// company_gst, and destination_of_supply from vendors + company_profile via vendor_id.
// Kept for reference / easy rollback:
// const getVendorById = async (vendorId: string) => {
//   const token = getStoredToken();
//   if (!token) {
//     throw new APIError(
//       "No authentication token found",
//       HTTP_STATUS.UNAUTHORIZED,
//     );
//   }
//
//   const response = await fetch(
//     `${API_BASE_URL}/api/bills/vendors/${vendorId}`,
//     {
//       method: "GET",
//       headers: {
//         Authorization: `Bearer ${token}`,
//         "Content-Type": "application/json",
//       },
//     },
//   );
//
//   if (!response.ok) {
//     throw new APIError(
//       `Failed to fetch vendor: ${response.statusText}`,
//       response.status,
//     );
//   }
//
//   return await response.json();
// };

/**
 * Validates a bill document against business rules
 * @param billData - The bill data to validate
 * @param billDocument - The bill document metadata
 * @param validationType - The type of validation: 'full_validation' or 'normal_validation'
 * @returns ValidationResponse with validation results
 */
export const validateBillDocument = async (
  billData: any,
  billDocument: any,
  validationType: string = "full_validation",
): Promise<ValidationResponse> => {
  try {
    // Token was only required for the frontend vendor fetch below (now commented out).
    // api_bill_validation.php does not use Bearer auth.
    // const token = getStoredToken();
    // if (!token) {
    //   throw new APIError(
    //     "No authentication token found",
    //     HTTP_STATUS.UNAUTHORIZED,
    //   );
    // }

    let isGstEnabled = true;
    const companyProfile = getStoredCompanyData();
    if (companyProfile && companyProfile.companyGstEnabled === "0") {
      isGstEnabled = false;
    }

    // Previously: fetched vendor from backend API and sent master fields in payload.
    // let vendor_gst = "";
    // let source_of_supply = "";
    // if (billData.vendor_id) {
    //   const vendor = await getVendorById(billData.vendor_id);
    //   vendor_gst = vendor.data.gstin ?? "";
    //   source_of_supply = vendor.data.state || vendor.data.country;
    // }

    // Send bill document state + vendor_id only; PHP enriches from DB (enrichBillDataFromMasters).
    const requestBody = {
      bill_data: {
        seller_gst: billData.vendor_gstin,
        // Bill document GSTIN only — never backfill from company profile
        buyer_gst: billData.customer_gstin ?? "",
        // ZOUR-65 R16: which GST registration this document is filed under, and
        // the pipeline's own match. THIS is the body the review pages actually
        // send — `services/api.ts::validateDocument` looks like the validation
        // call and nothing imports it, so additions there never reached the
        // screen. Without these two, cockpit-core cannot resolve the document's
        // location and none of the location flags can fire from the page.
        location_id: billData.location_id ?? null,
        location_match: billData.location_match ?? null,
        vendor_id: billData.vendor_id,
        gst_type: billDocument.gst_type,
        line_items: billData.line_items.map((item: any) => ({
          ...item,
          tax_id: item.tax_id ?? "",
          tax_percentage: item.tax_percentage ?? 0,
          tax_amount: isGstEnabled
            ? (item.item_total * (item.tax_percentage || 0)) / 100
            : 0,
        })),
        total: billData.total,
        bill_number: billData.bill_number,
        // ZOUR-19 (review 16): the document's own date. Without it cockpit-core
        // hit its "no usable document date" fallback on every review-time
        // validation, treated the bill as GST-applicable, ran the whole
        // GST/HSN/ITC set and could NEVER raise
        // `gst_not_applicable_before_date` — so the flag the feature adds was
        // invisible on the one screen a reviewer actually reads.
        // Both keys, because the extractor and the assembled Zoho JSON disagree
        // about which one holds it.
        date: billData.date || billData.invoice_date || "",
        invoice_date: billData.invoice_date || billData.date || "",
        // Master fields — resolved server-side in bill_validations.php:
        // company_gst: companyProfile?.gstNumber ?? "",
        // vendor_gst: vendor_gst,
        // source_of_supply: source_of_supply,
        // destination_of_supply: companyProfile?.state || billData.place_of_supply,
        //
        // ZOUR-65 R17: leaving destination_of_supply OUT is what makes the bill
        // side correct, and it must stay out. `enrichBillDataFromMasters()`
        // fills it from `getDocumentLocation()` — the registration this bill is
        // filed against — and only falls back to the company row when no
        // location can answer. Sending the company state here (as the invoice
        // twin used to) would win over that enrichment and re-create the false
        // "destination of supply does not match company location" on every
        // branch bill.
      },
      document_type: "bill",
      job_id: billDocument.jobid,
      validation_type: validationType,
    };

    const response = await fetch(`${CORE_API_URL}/api_bill_validation.php`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new APIError(
        `Failed to validate document: ${response.statusText}`,
        response.status,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error validating bill document:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};
